import React, { useState, useEffect, Suspense, useContext } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { DEFAULT_PALETTE_ID, paletteById } from './themes';
// Color imports removed (unused after themes cleanup)
import CssBaseline from "@mui/material/CssBaseline";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { UserProvider } from "./contexts/UserContext";
import { UserContext } from "./contexts/UserContext";
import { hasPageAccess } from "./config/pagePermissions";
import { PhaseProvider } from "./contexts/PhaseContext";
import { AccessProvider, useAccess } from "./contexts/AccessContext";
import { getHomePathForRole } from "./utils/getHomePathForRole";
import { usePhase } from "./contexts/PhaseContext";
import { PlatformProvider } from "./contexts/PlatformContext";
import { fetchJson } from "./utils/fetchJson";
import { useTranslation } from "react-i18next";
import { getThemeByName } from "./themes";
import { SnackbarProvider } from 'notistack';
import { Analytics } from "@vercel/analytics/react";

import ErrorBoundary from "./components/ErrorBoundary";
import OfflineIndicator from "./components/OfflineIndicator";
import UpdateAvailableSnackbar from "./components/UpdateAvailableSnackbar";
import PendingWritesIndicator from "./components/PendingWritesIndicator";
import OfflinePrimer from "./components/OfflinePrimer";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage";
import DashboardLayout from "./layouts/DashboardLayout";
import MyProfilePage from "./pages/MyProfilePage";

// School_Admin Pages
import ClassPromotionSummaryPage from "./pages/School_Admin/StudentManagement/ClassPromotionSummaryPage";
import StudentPromotionPage from "./pages/School_Admin/StudentPromotionPage";
import BudgetsPage from "./pages/School_Admin/Finance/BudgetsPage";
import CalendarPage from "./pages/School_Admin/CalendarPage";
import SchoolOverviewPage from "./pages/School_Admin/Dashboard/SchoolOverviewPage";
import QuickActionsPage from "./pages/School_Admin/Dashboard/QuickActionsPage";



import EvaluationTypesPage from "./pages/School_Admin/EvaluationTypesPage";
import ExpensesPage from "./pages/School_Admin/Finance/ExpensesPage";

import FeeTypesPage from "./pages/School_Admin/Finance/FeeTypesPage";
import GradingScalePage from "./pages/School_Admin/Academics/GradingScalePage";
// Removed: AcademicYearsPage
// Removed: SchoolDetailsPage, SystemSettingsPage
import UserDetailsPage from "./pages/School_Admin/StaffManagement/UserDetailsPage";
import UserFormPage from "./pages/School_Admin/StaffManagement/UserFormPage";
import UserListPage from "./pages/School_Admin/StaffManagement/UserListPage";
import AdmissionReviewPage from "./pages/School_Admin/StudentManagement/AdmissionReviewPage";
import AdmissionsPage from "./pages/School_Admin/StudentManagement/AdmissionsPage";
import IDCardWorkshopPage from "./pages/School_Admin/StudentManagement/IDCardWorkshopPage";
import TranscriptPage from "./pages/School_Admin/StudentManagement/TranscriptPage";

// New School Admin Pages
import LibraryDashboardPage from "./pages/School_Admin/Facilities/LibraryDashboardPage";
import InventoryControlPage from "./pages/School_Admin/Facilities/InventoryControlPage";
import TransportRoutesPage from "./pages/School_Admin/Facilities/TransportRoutesPage";
// Removed deleted Communication pages (NotificationCenterPage, PortalSettingsPage)
import SchoolSettingsPage from "./pages/School_Admin/SchoolSetupandConfiguration/SchoolSettingsPage";
import AcademicSessionsPage from "./pages/School_Admin/SchoolSetupandConfiguration/AcademicSessionsPage";
import RolePermissionPage from "./pages/School_Admin/StaffManagement/RolePermissionPage";
// Removed: LocalIntegrationsPage
import BackupSecurityPage from "./pages/School_Admin/SchoolSetupandConfiguration/BackupSecurityPage";
import CommunicationSettingsPage from "./pages/School_Admin/SchoolSetupandConfiguration/CommunicationSettingsPage";
// Removed: PortalConfigurationPage

// Finance Module Pages
import FeeStructureManagerPage from "./pages/School_Admin/Finance/FeeStructureManagerPage";
import PaymentProcessingPage from "./pages/School_Admin/Finance/PaymentProcessingPage";
import FinancialReportsPage from "./pages/School_Admin/Finance/FinancialReportsPage";
import ManageFeePaymentsPage from "./pages/School_Admin/Finance/ManageFeePaymentsPage";
import FeesPage from './pages/School_Admin/Finance/FeesPage';
import SalaryManagementPage from "./pages/School_Admin/Finance/SalaryManagementPage";
import SubscriptionRenewalPage from "./pages/School_Admin/Finance/SubscriptionRenewalPage";
import AttendanceSummaryTabsPage from "./pages/School_Admin/StudentManagement/AttendanceSummaryTabsPage";
import StudentsDisciplinePage from "./pages/School_Admin/StudentManagement/StudentsDisciplinePage";

// Teacher Pages
import ClassDisciplinePage from "./pages/Teacher/ClassDisciplinePage";
import ClassAcademicPerformancePage from "./pages/Teacher/ClassAcademicPerformancePage";
import TeacherMarksPage from "./pages/Teacher/TeacherMarksPage";
import TeacherDashboardPage from "./pages/Teacher/TeacherDashboardPage";
import AssignedSubjectsPage from "./pages/Teacher/AssignedSubjectsPage";
import AssignmentsPage from "./pages/Teacher/AssignmentsPage";
import TeacherAttendancePage from "./pages/Teacher/AttendancePage";
import SubjectReportPage from "./pages/SubjectHead/SubjectReportPage";

// Academics Module Pages
import TimetableDesignerPage from "./pages/School_Admin/Academics/TimetableDesignerPage";
import TimetableGeneratorPage from "./pages/School_Admin/Academics/TimetableGeneratorPage";
import EvaluationControlCenterPage from "./pages/School_Admin/Academics/EvaluationControlCenterPage";
import SubjectHubPage from "./pages/School_Admin/Academics/SubjectHubPage";
import ReportCardStudioPage from "./pages/School_Admin/Academics/ReportCardStudioPage";
import ExamControlCenterPage from "./pages/School_Admin/Academics/ExamControlCenterPage";

// Class Management Pages
import ClassManagementPage from "./pages/School_Admin/ClassManagement/ClassManagementPage";
import AddClassPage from "./pages/School_Admin/ClassManagement/AddClassPage";
import ManageClassesPage from "./pages/School_Admin/ClassManagement/ManageClassesPage";
import ClassAssignmentsPage from "./pages/School_Admin/ClassManagement/ClassAssignmentsPage";
import ClassReportsPage from "./pages/School_Admin/ClassManagement/ClassReportsPage";

// Subject Management Pages
import SubjectManagementPage from "./pages/School_Admin/SubjectManagement/SubjectManagementPage";
import AddSubjectPage from "./pages/School_Admin/SubjectManagement/AddSubjectPage";
import ManageSubjectsPage from "./pages/School_Admin/SubjectManagement/ManageSubjectsPage";
import SubjectAssignmentsPage from "./pages/School_Admin/SubjectManagement/SubjectAssignmentsPage";
import SubjectReportsPage from "./pages/School_Admin/SubjectManagement/SubjectReportsPage";

// Staff Management Pages
import StaffEvaluationPage from "./pages/School_Admin/StaffManagement/StaffEvaluationPage";
import StaffRatingsPage from "./pages/School_Admin/StaffManagement/StaffRatingsPage";
// Removed: PerformanceReviewsPage, TrainingDevelopmentPage, LeaveManagementPage

// Reports Pages
import AcademicReportsPage from "./pages/School_Admin/Reports/AcademicReportsPage";
import TranscriptsPage from "./pages/School_Admin/Reports/TranscriptsPage";

// School Settings Pages


// Removed deleted Communication Pages imports (Parents/Staff/Emergency)

import TuitionPage from "./pages/School_Admin/TuitionPage";
import HelpCenterPage from "./pages/School_Admin/Support/HelpCenterPage";
import SubmitTicketPage from "./pages/School_Admin/Support/SubmitTicketPage";
import MessagesCenterPage from "./pages/School_Admin/Communication/MessagesCenterPage";
// MyTicketsPage removed (unused - route redirects to submit-ticket)

// Parent Pages
import ParentDashboardPage from "./pages/Parent/Dashboard/ParentDashboardPage";
import ParentChildrenAcademicsPage from "./pages/Parent/ParentChildrenAcademicsPage";
import ParentAttendancePage from "./pages/Parent/ParentAttendancePage";
import ParentFeesPaymentsPage from "./pages/Parent/ParentFeesPaymentsPage";
import ParentMessagesPage from "./pages/Parent/ParentMessagesPage";

// Teacher Pages (duplicates removed and consolidated above)

// Super Admin Pages
import AnnouncementsPage from "./pages/Super_Admin/AnnouncementsPage";
import SchoolAnnouncementsPage from "./pages/School_Admin/Communication/AnnouncementsPage";
// AuditLogExportPage, ComplianceReportsPage, AuditTrailsPage used as tabs inside SecurityAuditPage (not routed directly)
import GlobalSettingsPage from "./pages/Super_Admin/SystemConfiguration/GlobalSettingsPage";
// LanguageSettingsPage, GlobalAcademicYearsPage used as tabs inside SystemSettingsPage (not routed directly)
import LicenseManagementPage from "./pages/Super_Admin/SystemConfiguration/LicenseManagementPage";
import SystemSettingsPage from "./pages/Super_Admin/SystemConfiguration/SystemSettingsPage";
import SystemCommunicationsPage from "./pages/Super_Admin/SystemConfiguration/SystemCommunicationsPage";
import SystemUsersPage from "./pages/Super_Admin/SystemConfiguration/SystemUsersPage";
import SupportTicketsPage from "./pages/Super_Admin/SystemConfiguration/SupportTicketsPage";

import SchoolAdminsPage from "./pages/Super_Admin/UsersManagement/SchoolAdminsPage";

import SubscriptionPlanPage from "./pages/Super_Admin/Billing&Subscriptions/SubscriptionPlanPage";
import InvoicesPage from "./pages/Super_Admin/Billing&Subscriptions/InvoicesPage";
// TaxConfigurationsPage removed (unused)
import BillingPage from "./pages/Super_Admin/Billing&Subscriptions/BillingPage";

import SystemOverviewPage from "./pages/Super_Admin/Dashboard/SystemOverviewPage";
import RegistrationQueuePage from "./pages/Super_Admin/SchoolsManagement/RegistrationQueuePage";
import OnboardingStatusPage from "./pages/Super_Admin/SchoolsManagement/OnboardingStatusPage";
import SchoolDirectoryPage from "./pages/Super_Admin/SchoolsManagement/SchoolDirectoryPage";
// PermissionsPage removed (unused - no route)
import SuperAdminsPage from "./pages/Super_Admin/UsersManagement/SuperAdminsPage";
import DataPolicyPage from "./pages/Super_Admin/SystemConfiguration/DataPolicyPage";
import UsersAccessPage from "./pages/Super_Admin/UsersManagement/UsersAccessPage";
import SecurityAuditPage from "./pages/Super_Admin/Security&Compliance/SecurityAuditPage";
import ITServicesPage from "./pages/Super_Admin/Integrations/ITServicesPage";
import IntegrationsPage from "./pages/Super_Admin/Integrations/IntegrationsPage";
import StudentDirectoryPage from "./pages/School_Admin/StudentManagement/StudentDirectoryPage";

// Simple PrivateRoute
function PrivateRoute({ children }) {
  const user = useContext(UserContext);
  const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
  
  // Wait for session check to complete before deciding
  if (user === undefined) return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>Verifying session…</div>;

  // If definitely no token or user context, redirect to login
  if (!token || user === null) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// WHERE A REFUSED PAGE SENDS A SIGNED-IN PERSON.
//
// Every guard here redirected to "/", and "/" is the LANDING page — the public
// marketing page. So each of the last few reports arrived as "the page opens the
// landing page", which sounds like a routing bug in the page itself and is
// really a refusal somewhere else. A signed-in person belongs at the top of
// their own sidebar; only a signed-out one belongs at "/".
function refusalTarget(user) {
  if (!user || !user.role) return '/';
  try {
    const privileges = Array.isArray(user.privileges) ? user.privileges : [];
    return getHomePathForRole(user.role, privileges) || '/';
  } catch (_) {
    return '/';
  }
}

function SecPhaseRoute({ children }) {
  const { activePhase } = usePhase();
  const location = useLocation();
  const user = useContext(UserContext);
  const { grantedByServer } = useAccess();

  const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '')
    .toString()
    .trim()
    .toLowerCase();
  const isSecPhase = phaseCode === 'sec';

  // A KNOWN PHASE DECIDES; an unknown one defers to the server.
  //
  // The sidebar shows a page when the server grants it, and if this guard then
  // refuses the click the person lands on "/" — the LANDING page — and reports
  // that their page "opens the landing page". That is why the server's grant
  // was allowed to win here, and while the phase is UNRESOLVED it still does.
  //
  // But it cannot win once the phase IS known and says nursery/primary. These
  // routes are secondary by construction and every endpoint behind them 403s
  // outside it, so letting a stale grant through only swaps a redirect for a
  // page full of errors. /api/permissions/my-access hands out that stale grant
  // more readily than it looks: AccessContext fetches the list early, and a
  // request made before PhaseContext has written the phase carries no
  // X-Active-Phase for the server to filter on — then the answer is cached.
  if (!phaseCode && grantedByServer(location.pathname)) return children;

  return isSecPhase ? children : <Navigate to={refusalTarget(user)} replace />;
}

// Simple permission-based route guard
function PermissionRoute({ pagePath, children }) {
  const user = useContext(UserContext);
  const location = useLocation();
  const { grantedByServer } = useAccess();
  
  // Wait for UserContext to load the session
  if (user === undefined) return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>Loading session…</div>;
  
  // If definitely no user, redirect to login
  if (user === null) return <Navigate to="/login" replace />;

  const currentPath = location.pathname;
  // Normalize path: remove trailing slash unless it's just '/'
  const normalizedCurrentPath = currentPath === '/' ? '/' : currentPath.replace(/\/+$/, '');
  const effectivePath = pagePath || normalizedCurrentPath;
  
  const privileges = user && Array.isArray(user.privileges) ? user.privileges : [];

  // THE SERVER'S ANSWER COUNTS HERE TOO, and it must, because the sidebar
  // already honours it. When only the menu consulted the server, a teacher saw
  // "Class results", clicked it, and this line sent them to the landing page —
  // a visible menu item that throws you out, which is worse than no item at all.
  // Union, so a failed /my-access changes nothing.
  const allowed = grantedByServer(effectivePath) || hasPageAccess(user.role, effectivePath, privileges);

  if (!allowed) {
    return <Navigate to={refusalTarget(user)} replace />;
  }
  
  return children;
}

function SystemAdminRoute({ children }) {
  const user = useContext(UserContext);
  if (user === undefined) return <div>Loading…</div>;
  if (user === null) return <Navigate to="/login" replace />;
  const role = (user?.role || '').toString();
  const allowed = ['SystemAdmin', 'ITManager', 'SalesRep', 'SupportAgent'].includes(role);
  return allowed ? children : <Navigate to="/" replace />;
}

function SystemAdminOutletRoute() {
  return (
    <SystemAdminRoute>
      <Outlet />
    </SystemAdminRoute>
  );
}

function RoutedApplication({ themeName, setThemeName }) {
  const location = useLocation();
  const resetKey = `${location.pathname}${location.search}${location.hash}`;

  return (
    <ErrorBoundary resetKey={resetKey}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <UserProvider>
          <OfflinePrimer />
          <PhaseProvider>
            <AccessProvider>
            <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/terms" element={<TermsAndConditionsPage />} />
                <Route path="/login" element={<LoginPage />} />
                
                {/* Protected routes wrapped with DashboardLayout */}
                <Route element={<PrivateRoute><DashboardLayout themeName={themeName} onThemeChange={setThemeName} /></PrivateRoute>}>
                  {/* Dashboard */}
                  <Route path="/school-overview" element={<PermissionRoute pagePath="/school-overview"><SchoolOverviewPage /></PermissionRoute>} />
                  <Route path="/quick-actions" element={<PermissionRoute pagePath="/quick-actions"><QuickActionsPage /></PermissionRoute>} />

                  {/* Student Management */}
                  <Route path="/class-promotion-summary" element={<PermissionRoute pagePath="/class-promotion-summary"><ClassPromotionSummaryPage /></PermissionRoute>} />
                  <Route path="/student-promotion" element={<PermissionRoute pagePath="/student-promotion"><StudentPromotionPage /></PermissionRoute>} />
                  <Route path="/attendance" element={<PermissionRoute pagePath="/attendance"><AttendanceSummaryTabsPage /></PermissionRoute>} />
                  <Route path="/attendance-summary" element={<PermissionRoute pagePath="/attendance-summary"><AttendanceSummaryTabsPage /></PermissionRoute>} />
                  <Route path="/students-discipline" element={<PermissionRoute pagePath="/students-discipline"><StudentsDisciplinePage /></PermissionRoute>} />
                  <Route path="/student-admissions" element={<PermissionRoute pagePath="/student-admissions"><AdmissionsPage /></PermissionRoute>} />
                  <Route path="/student-directory" element={<PermissionRoute pagePath="/student-directory"><StudentDirectoryPage /></PermissionRoute>} />
                  <Route path="/id-card-workshop" element={<PermissionRoute pagePath="/id-card-workshop"><IDCardWorkshopPage /></PermissionRoute>} />
                  <Route path="/student-transcripts" element={<SecPhaseRoute><PermissionRoute pagePath="/student-transcripts"><TranscriptPage /></PermissionRoute></SecPhaseRoute>} />
                  <Route path="/admission-review" element={<PermissionRoute pagePath="/admission-review"><AdmissionReviewPage /></PermissionRoute>} />
                  
                  {/* Academics & Exams */}
                  <Route path="/calendar" element={<PermissionRoute pagePath="/calendar"><CalendarPage /></PermissionRoute>} />
                  <Route path="/timetable" element={<PermissionRoute pagePath="/timetable"><TimetableDesignerPage /></PermissionRoute>} />
                  <Route path="/timetable-generator" element={<PermissionRoute pagePath="/timetable-generator"><TimetableGeneratorPage /></PermissionRoute>} />
                  <Route path="/evaluation-control-center" element={<PermissionRoute pagePath="/evaluation-control-center"><EvaluationControlCenterPage /></PermissionRoute>} />
                  <Route path="/evaluation-types" element={<PermissionRoute pagePath="/evaluation-types"><EvaluationTypesPage /></PermissionRoute>} />
                  <Route path="/subject-hub" element={<PermissionRoute pagePath="/subject-hub"><SubjectHubPage /></PermissionRoute>} />
                  <Route path="/report-card-studio" element={<PermissionRoute pagePath="/report-card-studio"><ReportCardStudioPage /></PermissionRoute>} />
                  <Route path="/exam-control-center" element={<PermissionRoute pagePath="/exam-control-center"><ExamControlCenterPage /></PermissionRoute>} />
                  <Route path="/grading-scales" element={<PermissionRoute pagePath="/grading-scales"><GradingScalePage /></PermissionRoute>} />
                  <Route path="/discipline" element={<PermissionRoute pagePath="/discipline"><StudentsDisciplinePage /></PermissionRoute>} />

                  {/* Parent Routes */}
                  <Route path="/parent/dashboard" element={<PermissionRoute pagePath="/parent/dashboard"><ParentDashboardPage /></PermissionRoute>} />
                  <Route path="/parent/children-academics" element={<PermissionRoute pagePath="/parent/children-academics"><ParentChildrenAcademicsPage /></PermissionRoute>} />
                  <Route path="/parent/attendance" element={<PermissionRoute pagePath="/parent/attendance"><ParentAttendancePage /></PermissionRoute>} />
                  <Route path="/parent/fees-payments" element={<PermissionRoute pagePath="/parent/fees-payments"><ParentFeesPaymentsPage /></PermissionRoute>} />
                  <Route path="/parent/messages" element={<Navigate to="/communication/messages" replace />} />
                  <Route path="/parent/messages-old" element={<PermissionRoute pagePath="/parent/messages"><ParentMessagesPage /></PermissionRoute>} />

                  {/* Finance */}
                  <Route path="/fee-types" element={<PermissionRoute pagePath="/fee-types"><FeeTypesPage /></PermissionRoute>} />
                  <Route path="/fee-structure-manager" element={<PermissionRoute pagePath="/fee-structure-manager"><FeeStructureManagerPage /></PermissionRoute>} />
                  {/* Both fee paths render the merged Fees page. They were two
                      pages asking different questions of one dataset, and both
                      could approve a parent's receipt. Kept as two routes so a
                      role granted only one still reaches the whole page. */}
                  <Route path="/payment-processing" element={<PermissionRoute pagePath="/payment-processing"><FeesPage /></PermissionRoute>} />
                  <Route path="/manage-fee-payments" element={<PermissionRoute pagePath="/manage-fee-payments"><FeesPage /></PermissionRoute>} />
                  <Route path="/budgets" element={<PermissionRoute pagePath="/budgets"><BudgetsPage /></PermissionRoute>} />
                  <Route path="/expenses" element={<PermissionRoute pagePath="/expenses"><ExpensesPage /></PermissionRoute>} />
                  <Route path="/salary-management" element={<PermissionRoute pagePath="/salary-management"><SalaryManagementPage /></PermissionRoute>} />
                  <Route path="/financial-reports" element={<PermissionRoute pagePath="/financial-reports"><FinancialReportsPage /></PermissionRoute>} />
                  <Route path="/tuition" element={<PermissionRoute pagePath="/tuition"><TuitionPage /></PermissionRoute>} />
                  <Route path="/finance/subscription-renewal" element={<PermissionRoute pagePath="/finance/subscription-renewal"><SubscriptionRenewalPage /></PermissionRoute>} />

                  {/* Class Management Pages */}
                  <Route path="/class-management" element={<PermissionRoute pagePath="/class-management"><ClassManagementPage /></PermissionRoute>} />
                  <Route path="/class-management/add-class" element={<PermissionRoute pagePath="/class-management/add-class"><AddClassPage /></PermissionRoute>} />
                  <Route path="/class-management/manage-classes" element={<PermissionRoute pagePath="/class-management/manage-classes"><ManageClassesPage /></PermissionRoute>} />
                  <Route path="/class-management/class-assignments" element={<PermissionRoute pagePath="/class-management/class-assignments"><ClassAssignmentsPage /></PermissionRoute>} />
                  <Route path="/class-management/class-reports" element={<PermissionRoute pagePath="/class-management/class-reports"><ClassReportsPage /></PermissionRoute>} />

                  {/* Subject Management Pages */}
                  <Route path="/subject-management" element={<PermissionRoute pagePath="/subject-management"><SubjectManagementPage /></PermissionRoute>} />
                  <Route path="/subject-management/add-subject" element={<PermissionRoute pagePath="/subject-management/add-subject"><AddSubjectPage /></PermissionRoute>} />
                  <Route path="/subject-management/manage-subjects" element={<PermissionRoute pagePath="/subject-management/manage-subjects"><ManageSubjectsPage /></PermissionRoute>} />
                  <Route path="/subject-management/subject-assignments" element={<PermissionRoute pagePath="/subject-management/subject-assignments"><SubjectAssignmentsPage /></PermissionRoute>} />
                  <Route path="/subject-management/subject-reports" element={<PermissionRoute pagePath="/subject-management/subject-reports"><SubjectReportsPage /></PermissionRoute>} />

                  {/* Staff Management */}
                  <Route path="/user-list" element={<PermissionRoute pagePath="/user-list"><UserListPage /></PermissionRoute>} />
                  <Route path="/user-form" element={<PermissionRoute pagePath="/user-form"><UserFormPage /></PermissionRoute>} />
                  <Route path="/user-details" element={<PermissionRoute pagePath="/user-details"><UserDetailsPage /></PermissionRoute>} />
                  <Route path="/users/new" element={<PermissionRoute pagePath="/user-form"><UserFormPage /></PermissionRoute>} />
                  <Route path="/users/:userId/edit" element={<PermissionRoute pagePath="/user-form"><UserFormPage /></PermissionRoute>} />
                  <Route path="/role-permissions" element={<PermissionRoute pagePath="/role-permissions"><RolePermissionPage /></PermissionRoute>} />
                  <Route path="/staff-evaluation" element={<PermissionRoute pagePath="/staff-evaluation"><StaffEvaluationPage /></PermissionRoute>} />
                  <Route path="/staff-ratings" element={<PermissionRoute pagePath="/staff-evaluation"><StaffRatingsPage /></PermissionRoute>} />

                  {/* Facilities */}
                  <Route path="/facilities/library-dashboard" element={<PermissionRoute pagePath="/facilities/library-dashboard"><LibraryDashboardPage /></PermissionRoute>} />
                  <Route path="/facilities/inventory-control" element={<PermissionRoute pagePath="/facilities/inventory-control"><InventoryControlPage /></PermissionRoute>} />
                  <Route path="/facilities/transport-routes" element={<PermissionRoute pagePath="/facilities/transport-routes"><TransportRoutesPage /></PermissionRoute>} />
                  
                  {/* Additional Reports Routes */}
                  <Route path="/reports/financial" element={<PermissionRoute pagePath="/reports/financial"><FinancialReportsPage /></PermissionRoute>} />
                  <Route path="/reports/academic" element={<PermissionRoute pagePath="/reports/academic"><AcademicReportsPage /></PermissionRoute>} />
                  <Route path="/reports/transcripts" element={<PermissionRoute pagePath="/reports/transcripts"><TranscriptsPage /></PermissionRoute>} />
                  
                  {/* Teacher Routes */}
                  <Route path="/teacher/assigned-subjects" element={<PermissionRoute pagePath="/teacher/assigned-subjects"><AssignedSubjectsPage /></PermissionRoute>} />
                  <Route path="/teacher/dashboard" element={<PermissionRoute pagePath="/teacher/dashboard"><TeacherDashboardPage /></PermissionRoute>} />
                  {/* MARK ENTRY ANSWERS TO ITS OWN PERMISSION PATH.
                      `/teacher/student-grading` is the canonical name: it is the
                      row in PAGE_PERMISSIONS, it is the path this very route
                      already checks, and it is what the phone registers. But no
                      ROUTE existed for it — the page was mounted only at
                      `/teacher/marks` — so every link using the canonical name
                      fell through to the catch-all and landed on the marketing
                      page. Three did: the "Enter marks" button on the teacher
                      dashboard, and both School Admin quick actions.
                      `/teacher/marks` stays as an alias so existing links and
                      bookmarks keep working. */}
                  <Route path="/teacher/student-grading" element={<PermissionRoute pagePath="/teacher/student-grading"><TeacherMarksPage /></PermissionRoute>} />
                  <Route path="/teacher/marks" element={<PermissionRoute pagePath="/teacher/student-grading"><TeacherMarksPage /></PermissionRoute>} />
                  <Route path="/teacher/attendance-recording" element={<PermissionRoute pagePath="/teacher/attendance-recording"><TeacherAttendancePage /></PermissionRoute>} />
                  
                  {/* ClassMaster Routes */}
                  <Route path="/classmaster/class-discipline" element={<PermissionRoute pagePath="/classmaster/class-discipline"><ClassDisciplinePage /></PermissionRoute>} />
                  <Route path="/classmaster/class-academic-performance" element={<PermissionRoute pagePath="/classmaster/class-academic-performance"><ClassAcademicPerformancePage /></PermissionRoute>} />
                  
                  {/* SubjectHead Routes */}

                  {/* BOTH HALVES ARE SECONDARY-ONLY. I removed this guard on
                      the reasoning that a department is scoped to a department,
                      so a nursery or primary head would hold one too. That was
                      my invention. Head of department is a SECONDARY role in
                      this school system — nursery and primary have class
                      teachers and subject teachers, not departments — and the
                      codebase said so in four separate places before I
                      overrode all four. */}
                  {/* TWO PAGES, NOT TWO TABS — the split the phone has always
                      had (SubjectPerformanceScreen, DepartmentalReportScreen).
                      Marks analysis and monthly paperwork are unrelated jobs,
                      and each entry now opens only its own. */}
                  <Route path="/subjecthead/subject-report" element={<SecPhaseRoute><PermissionRoute pagePath="/subjecthead/subject-report"><SubjectReportPage mode="reports" /></PermissionRoute></SecPhaseRoute>} />
                  <Route path="/subjecthead/subject-performance-matrix" element={<SecPhaseRoute><PermissionRoute pagePath="/subjecthead/subject-performance-matrix"><SubjectReportPage mode="performance" /></PermissionRoute></SecPhaseRoute>} />

                  {/* Teacher legacy aliases */}
                  <Route path="/teacher/attendance" element={<PermissionRoute pagePath="/teacher/attendance-recording"><TeacherAttendancePage /></PermissionRoute>} />
                  <Route path="/teacher/class-discipline" element={<PermissionRoute pagePath="/classmaster/class-discipline"><ClassDisciplinePage /></PermissionRoute>} />
                  <Route path="/teacher/class-academic-performance" element={<PermissionRoute pagePath="/classmaster/class-academic-performance"><ClassAcademicPerformancePage /></PermissionRoute>} />
                  
                  {/* Student Routes */}
                  <Route path="/student/messages" element={<PermissionRoute pagePath="/student/messages"><MessagesCenterPage /></PermissionRoute>} />
                  <Route path="/student/profile" element={<PermissionRoute pagePath="/student/profile"><MyProfilePage /></PermissionRoute>} />

                  {/* Profile & Settings */}
                  <Route path="/my-profile" element={<PermissionRoute pagePath="/my-profile"><MyProfilePage /></PermissionRoute>} />
                  
                  {/* Communication */}
                  {/* THE SCHOOL ADMIN'S page, not the platform's. This route rendered
                      Super_Admin/AnnouncementsPage — a view built to look ACROSS
                      schools, which is why a school administrator was shown
                      "Engagement by School" and a target-schools picker for schools
                      that are not theirs. The school page has existed all along and
                      was never routed to. */}
                  <Route path="/communication/announcements" element={<PermissionRoute pagePath="/communication/announcements"><SchoolAnnouncementsPage /></PermissionRoute>} />
                  <Route path="/communication/messages" element={<PermissionRoute pagePath="/communication/messages"><MessagesCenterPage /></PermissionRoute>} />

                  {/* School Setup & Configuration */}
                  <Route path="/school-setup-and-configuration/school-settings" element={<PermissionRoute pagePath="/school-setup-and-configuration/school-settings"><SchoolSettingsPage /></PermissionRoute>} />
                  <Route path="/school-setup-and-configuration/communication-settings" element={<PermissionRoute pagePath="/school-setup-and-configuration/communication-settings"><CommunicationSettingsPage /></PermissionRoute>} />
                  <Route path="/school-setup-and-configuration/backup-security" element={<PermissionRoute pagePath="/school-setup-and-configuration/backup-security"><BackupSecurityPage /></PermissionRoute>} />
                  <Route path="/school-setup-and-configuration/academic-sessions" element={<PermissionRoute pagePath="/school-setup-and-configuration/academic-sessions"><AcademicSessionsPage /></PermissionRoute>} />

                  {/* Legacy School Settings Routes */}
                  <Route path="/school-settings/permissions" element={<PermissionRoute pagePath="/role-permissions"><RolePermissionPage /></PermissionRoute>} />

                  {/* Support & Maintenance Routes */}
                  <Route path="/support/submit-ticket" element={<PermissionRoute pagePath="/support/submit-ticket"><SubmitTicketPage /></PermissionRoute>} />
                  <Route path="/support/my-tickets" element={<PermissionRoute pagePath="/support/my-tickets"><Navigate to="/support/submit-ticket" replace /></PermissionRoute>} />
                  <Route path="/support/help-center" element={<PermissionRoute pagePath="/support/help-center"><HelpCenterPage /></PermissionRoute>} />
                  <Route path="/support/help" element={<Navigate to="/support/help-center" replace />} />

                  {/* Super Admin Area */}
                  <Route path="/super-admin" element={<SystemAdminOutletRoute />}>
                    <Route path="system-overview" element={<PermissionRoute pagePath="/super-admin/system-overview"><SystemOverviewPage /></PermissionRoute>} />
                    <Route path="schools" element={<PermissionRoute pagePath="/super-admin/schools"><SchoolDirectoryPage /></PermissionRoute>} />
                    <Route path="billing" element={<PermissionRoute pagePath="/super-admin/billing"><BillingPage /></PermissionRoute>} />
                    <Route path="system-settings" element={<PermissionRoute pagePath="/super-admin/system-settings"><SystemSettingsPage /></PermissionRoute>} />
                    <Route path="communications" element={<PermissionRoute pagePath="/super-admin/communications"><SystemCommunicationsPage /></PermissionRoute>} />
                    <Route path="system-users" element={<PermissionRoute pagePath="/super-admin/system-users"><SystemUsersPage /></PermissionRoute>} />
                    <Route path="support-tickets" element={<PermissionRoute pagePath="/super-admin/support-tickets"><SupportTicketsPage /></PermissionRoute>} />
                    <Route path="registration-queue" element={<PermissionRoute pagePath="/super-admin/registration-queue"><RegistrationQueuePage /></PermissionRoute>} />
                    <Route path="onboarding-status" element={<PermissionRoute pagePath="/super-admin/onboarding-status"><OnboardingStatusPage /></PermissionRoute>} />
                    <Route path="super-admins" element={<PermissionRoute pagePath="/super-admin/super-admins"><SuperAdminsPage /></PermissionRoute>} />
                    <Route path="school-admins" element={<PermissionRoute pagePath="/super-admin/school-admins"><SchoolAdminsPage /></PermissionRoute>} />
                    <Route path="license-management" element={<PermissionRoute pagePath="/super-admin/license-management"><LicenseManagementPage /></PermissionRoute>} />
                    <Route path="data-policies" element={<PermissionRoute pagePath="/super-admin/data-policies"><DataPolicyPage /></PermissionRoute>} />
                    <Route path="global-settings" element={<PermissionRoute pagePath="/super-admin/global-settings"><GlobalSettingsPage themeName={themeName} onThemeChange={setThemeName} /></PermissionRoute>} />
                    <Route path="subscription-plans" element={<PermissionRoute pagePath="/super-admin/subscription-plans"><SubscriptionPlanPage /></PermissionRoute>} />
                    <Route path="payment-processing" element={<PermissionRoute pagePath="/super-admin/payment-processing"><PaymentProcessingPage /></PermissionRoute>} />
                    <Route path="invoices" element={<PermissionRoute pagePath="/super-admin/invoices"><InvoicesPage /></PermissionRoute>} />
                    <Route path="users-access" element={<PermissionRoute pagePath="/super-admin/users-access"><UsersAccessPage /></PermissionRoute>} />
                    <Route path="security" element={<PermissionRoute pagePath="/super-admin/security"><SecurityAuditPage /></PermissionRoute>} />
                    <Route path="integrations" element={<PermissionRoute pagePath="/super-admin/integrations"><IntegrationsPage /></PermissionRoute>} />
                    <Route path="it/dashboard" element={<PermissionRoute pagePath="/super-admin/it/dashboard"><ITServicesPage /></PermissionRoute>} />
                    <Route path="it/maintenance" element={<PermissionRoute pagePath="/super-admin/it/maintenance"><ITServicesPage /></PermissionRoute>} />
                    <Route path="it/monitoring" element={<PermissionRoute pagePath="/super-admin/it/monitoring"><ITServicesPage /></PermissionRoute>} />
                    <Route path="it/backup" element={<PermissionRoute pagePath="/super-admin/it/backup"><ITServicesPage /></PermissionRoute>} />
                    <Route path="it/security" element={<PermissionRoute pagePath="/super-admin/it/security"><ITServicesPage /></PermissionRoute>} />
                    <Route path="it/api" element={<PermissionRoute pagePath="/super-admin/it/api"><ITServicesPage /></PermissionRoute>} />
                  </Route>

                  {/* Catch-all for authenticated users */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>

                {/* Catch-all for everyone else */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </SnackbarProvider>
            </AccessProvider>
          </PhaseProvider>
        </UserProvider>
      </motion.div>
    </ErrorBoundary>
  );
}

function App() {
  const { i18n } = useTranslation();
  // One of the four palettes. Every id this app has ever stored here — the
  // fifteen themes, `encatar`, and the six palettes that came between — is gone
  // now; anything unrecognised falls through paletteById to the default, which
  // is why none of those need clearing out of anyone's browser.
  const [themeName, setThemeName] = useState(
    localStorage.getItem("theme") || DEFAULT_PALETTE_ID
  );

  useEffect(() => {
    localStorage.setItem("theme", themeName);
    // THE BROWSER'S OWN CHROME, which is the web's version of the phone's
    // navigation bar. On Android Chrome and in an installed PWA the address bar
    // and the status bar above the page are painted from this meta tag; index.html
    // ships a fixed #2B2880 that belongs to none of the four palettes, so the
    // band above the page stayed one colour while everything below it changed.
    // Same complaint as the mobile navigation bar, same fix: paint it from the
    // palette that is actually on.
    try {
      const tag = document.querySelector('meta[name="theme-color"]');
      if (tag) tag.setAttribute('content', paletteById(themeName).deep);
    } catch (_) { /* no document, or the tag was removed */ }
  }, [themeName]);

  // Load per-user settings (theme, language) on app start and when notified
  useEffect(() => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetchJson('/user-settings/settings');
        if (res && res.success && res.data) {
          const s = res.data;
          if (s.theme) setThemeName(s.theme);
          if (s.language && s.language !== i18n.language) {
            try { i18n.changeLanguage(s.language); } catch (_) {}
          }
        }
      } catch (_) {}
    })();

    const onSettingsUpdated = (e) => {
      const detail = (e && e.detail) || {};
      if (detail.theme) setThemeName(detail.theme);
      if (detail.language && detail.language !== i18n.language) {
        try { i18n.changeLanguage(detail.language); } catch (_) {}
      }
    };
    window.addEventListener('app:user:settings:updated', onSettingsUpdated);
    return () => window.removeEventListener('app:user:settings:updated', onSettingsUpdated);
  }, [i18n]);

  const theme = getThemeByName(themeName);

  return (
    <PlatformProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={<div>Loading…</div>}>
        <BrowserRouter>
          <RoutedApplication themeName={themeName} setThemeName={setThemeName} />
          <OfflineIndicator />
          <PendingWritesIndicator />
          <UpdateAvailableSnackbar />
          <Analytics />
        </BrowserRouter>
      </Suspense>
    </ThemeProvider>
    </PlatformProvider>
  );
}

export default App;
